import {
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  SxProps,
  TextField,
} from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Calendar } from 'asset';
import { Moment } from 'moment';
import { Fragment, useState } from 'react';
import { Controller } from 'react-hook-form';

interface TimerPickerFieldProps {
  title?: string;
  type?: string;
  name: string;
  form?: any;
  rules?: any;
  value?: Moment | any;
  disabled?: boolean;
  placeholder?: string;
  rows?: number;
  sx?: SxProps;
  errorsName?: string;
  readOnly?: boolean;
  minTime?: Moment;
  maxTime?: Moment;
}

const TimerPicker = ({
  type,
  title,
  value,
  form,
  name,
  rules,
  rows,
  placeholder,
  errorsName,
  readOnly,
  disabled = false,
  minTime,
  maxTime,
  sx,
  ...rest
}: TimerPickerFieldProps) => {
  const {
    control,
    formState: { errors },
  } = form;
  const [openDate, setOpenDate] = useState<boolean>(false);

  return (
    <Fragment>
      <InputLabel
        sx={{
          fontWeight: 500,
          fontSize: '15px',
          lineHeight: '100%',
          color: '#000',
          marginBottom: '5px',
        }}
      >
        {title}
      </InputLabel>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Controller
          name={name}
          control={control}
          rules={rules}
          render={({ field }) => (
            <TimePicker
              {...field}
              value={field.value ?? null}
              inputFormat="HH:mm"
              open={openDate}
              readOnly={readOnly || disabled}
              onOpen={() => setOpenDate(true)}
              onClose={() => setOpenDate(false)}
              minTime={minTime}
              maxTime={maxTime}
              renderInput={(params) => (
                <TextField
                  disabled={disabled}
                  {...params}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        <IconButton
                          disabled={disabled}
                          onClick={() => setOpenDate(true)}
                          sx={{
                            '&:hover': {
                              bgcolor: 'transparent',
                            },
                            '& .MuiTouchRipple-root': {
                              display: 'none',
                            },
                            position: 'absolute',
                            top: -5,
                            left: -16,
                            borderRadius: '0px',
                            cursor: disabled ? 'default' : 'pointer',
                            width: '230px',
                          }}
                        >
                          <Calendar color="transparent" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    background: disabled ? '#eaeaea' : '#fff',
                    borderRadius: '5px',
                    height: '38px',
                    p: '16px',
                    display: 'flex',
                    justifyContent: 'center',
                    border: '1px solid #D5D5D5',
                    '& >.MuiInput-root>.MuiInput-input': {
                      fontWeight: 400,
                      fontSize: '13px',
                      lineHeight: '100%',
                    },
                    '& >.MuiInput-root:before': { display: 'none' },
                    '& >.MuiInput-root:after': { display: 'none' },
                  }}
                  variant="standard"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
              {...rest}
            />
          )}
        />
      </LocalizationProvider>

      {!!(errors as any)[name] ? (
        <FormHelperText
          error
          sx={{
            fontSize: '14px',
            letterSpacing: '0.045em',
            color: 'white.light',
          }}
        >
          {(errors as any)?.[name]?.message || ''}
        </FormHelperText>
      ) : (
        <FormHelperText
          error
          sx={{
            fontSize: '14px',
            letterSpacing: '0.045em',
            color: 'white.light',
          }}
        >
          {errorsName || ''}
        </FormHelperText>
      )}
    </Fragment>
  );
};

export default TimerPicker;
